<template>
  <div>
    <b-modal
      :id="id"
      ref="processingModal"
      size="xs"
      ok-variant="-primary btn-sm"
      cancel-variant="-primary btn__outline btn-sm"
      :title="`${$t('component.modalConfirmation.title')} - ${selectedSample && selectedSample.sampleLabel}`"
      centered
      :ok-title="$t('component.modalConfirmation.okTitle')"
      :cancel-title="$t('component.modalConfirmation.cancelTitle')"
      @ok="processAndSelectNextSample(true)"
      @cancel="processingFinished"
    >
      <div
        class="m-2 secondary-color"
      >
        {{ $t('sampleProcessing.differentResponsibleForProcessing', { action:actionTranslation }) }}
      </div>
      <b-form-checkbox
        v-if="showAgainCheckboxEnabled"
        v-model="dontShowAgain"
        :value="true"
        :unchecked-value="false"
      >
        {{ $t('component.modalConfirmation.dontShowAgain') }}
      </b-form-checkbox>
    </b-modal>
    <samples-update-util ref="samplesUpdateUtil" />
  </div>
</template>

<script>
import sampleProcessingSampleService from '@/modules/sampleProcessingModule/services/sampleProcessingSampleService'
import SamplesUpdateUtil from '@/components/common/SamplesUpdateUtil'
import { isNil } from 'lodash'
import laboratoryDynamicServices from '@/modules/laboratoryModule/services/laboratoryDynamicServices'

export default {
    name: 'SampleBatchProcessing',
    components: {
        SamplesUpdateUtil
    },
    props: {
        isGrossingRoute: { type: Boolean, required: true }
    },
    data() {
        return {
            selectedSample: null,
            samples: [],
            sampleIndex: null,
            dontShowAgain: false,
            showAgainCheckboxEnabled: true,
            id: 'caseValidationModal',
            finalErrorMessages: [],
            atLeastOneSampleHasBeenProcessed: false,
            shouldAssignMyselfAsResponsibleSaved: false,
            selectedOption: null
        }
    },
    computed: {
        laboratoryId() {
            return this.$store.getters.labId
        },
        isResponsibleForGrossingValid() {
            return this.$store.getters.userInformation?.personnel?.id === this.selectedSample.grossingResponsible?.id
        },
        actionTranslation() {
            if (this.isGrossingRoute) {
                return this.$t('sampleProcessing.grossing')
            }
            return this.$t('sampleProcessing.processing')
        }
    },
    methods: {
        async processAndSelectNextSample(shouldAssignMyselfAsResponsible) {
            if (this.selectedOption.isSampleProcessingOperation) {
                if (shouldAssignMyselfAsResponsible || (this.dontShowAgain && this.shouldAssignMyselfAsResponsibleSaved)) {
                    await this.assignLoggedUserAsResponsibleForGrossing()
                } else if (isNil(shouldAssignMyselfAsResponsible) && !this.isResponsibleForGrossingValid && !this.dontShowAgain) {
                    this.$refs.processingModal.show()
                    return
                }
                this.shouldAssignMyselfAsResponsibleSaved = shouldAssignMyselfAsResponsible
                if (this.isResponsibleForGrossingValid) {
                    await this.internalProcessSample(this.selectedSample, this.selectedOption)
                }
            } else {
                await this.internalProcessSample(this.selectedSample, this.selectedOption)
            }
            if (this.sampleIndex !== (this.samples.length - 1)) {
                this.sampleIndex++
                this.selectedSample = this.samples[this.sampleIndex]
                await this.processAndSelectNextSample()
            } else {
                this.processingFinished()
            }
        },
        processingFinished() {
            if (this.finalErrorMessages.length) {
                this.showErrorModalGlobal(this.finalErrorMessages.join('<br/>'))
            } else if (this.atLeastOneSampleHasBeenProcessed) {
                this.showSuccessModalGlobal(this.$t('laboratory.content.actionOk'))
            }
            this.$eventHub.$emit('updateTaskCount')
            this.$emit('samplesUpdated')
            this.$_cleanComponentData()
        },
        async processSamples(samples, selectedOption) {
            this.initializeData(samples, selectedOption)
            if (selectedOption.isSampleProcessingOperation && !this.isResponsibleForGrossingValid) {
                this.showAgainCheckboxEnabled = false
                this.$refs.processingModal.show()
            } else {
                await this.processAndSelectNextSample()
            }
        },
        async internalProcessSample(sample, sidebarOption) {
            this.updateLoading(true)
            let error
            if (sidebarOption.isSingleElementOperation) {
                if (sidebarOption.isSampleProcessingOperation) {
                    [error] = await this.toCall(sampleProcessingSampleService.process(
                        this.laboratoryId, sample.caseStudy.casId, sample.id))
                } else {
                    [error] = await this.toCall(laboratoryDynamicServices.applyNonBatchAction(
                        this.laboratoryId, sample.caseStudy.casId, sample.id, sidebarOption.actionUrl))
                }
            } else {
                const params = [{
                    caseId: sample.caseStudy.casId,
                    sampleId: sample.id
                }];
                [error] = await this.toCall(laboratoryDynamicServices.applyAction(
                    this.laboratoryId, sidebarOption.sectionId, sidebarOption.actionUrl, params))
            }
            this.updateLoading(false)
            if (!error) {
                this.atLeastOneSampleHasBeenProcessed = true
            } else {
                if (error.response && error.response.data.code === this.$apiExceptions.SAMPLE_HAS_PORTIONS_NOT_VERIFIED_AS_CLOSED) {
                    this.finalErrorMessages.push(`${this.selectedSample.sampleLabel} - ${this.$t('grossing.error.portionsNotVerifiedAsClosed')}`)
                } else if (error.response && error.response.data.messageTranslated) {
                    this.finalErrorMessages.push(`${error.response.data.message}`)
                } else {
                    this.finalErrorMessages.push(`${this.selectedSample.sampleLabel} - ${this.$t('component.modalError.title')}`)
                }
            }
        },
        async assignLoggedUserAsResponsibleForGrossing() {
            const sampleToUpdate = {
                grossingResponsibleId: this.$store.getters.userInformation?.personnel?.id,
                caseId: this.selectedSample.caseStudy.casId,
                sampleId: this.selectedSample.id,
                originalConcurrencyUpdatedAt: this.selectedSample.originalConcurrencyUpdatedAt
            }
            this.updateLoading(true)
            const [error, updateSampleResponse] = await this.$refs.samplesUpdateUtil.updateSample(
                this.laboratoryId, this.selectedSample.caseStudy.casId, this.selectedSample.id,
                sampleToUpdate, sampleProcessingSampleService
            )
            this.updateLoading(false)

            if (!error) {
                this.selectedSample.grossingResponsible = updateSampleResponse.grossingResponsible
            } else if (error.response && error.response.data.messageTranslated) {
                this.finalErrorMessages.push(`${this.selectedSample.sampleLabel} - ${error.response.data.message}`)
            } else {
                this.finalErrorMessages.push(`${this.selectedSample.sampleLabel} - ${this.$t('component.modalError.title')}`)
            }
        },
        initializeData(samples, selectedOption) {
            this.selectedOption = selectedOption
            this.sampleIndex = 0
            this.samples = samples
            this.dontShowAgain = samples.length === 1
            this.selectedSample = samples[this.sampleIndex]
            this.errorMessage = ''
            this.finalErrorMessages = []
        }
    }
}
</script>

<style scoped>

</style>
